<script setup>
import { ChannelIcon } from '@dashhudson/dashing-ui/channel-icon';
import { Chip } from '@dashhudson/dashing-ui/chip';
import { Column, DataTable } from '@dashhudson/dashing-ui/data-table';
import { ProgressSpinner } from '@dashhudson/dashing-ui/progress-spinner';
import { RowTitle } from '@dashhudson/dashing-ui/row-title';
import { SentimentSelect } from '@dashhudson/dashing-ui/sentiment-select';

const props = defineProps({
  posts: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  drawerId: { type: String, required: true },
  onRowClick: { type: Function, required: true },
  canLoadMore: { type: Function, required: true },
  onLoadMore: { type: Function, required: true },
  emptyState: { type: Object, default: () => ({ message: 'No data found' }) },
  captionHeader: { type: String, default: 'Caption/Comment' },
  onSentimentUpdate: { type: Function, default: null },
  isSentimentLoading: { type: Function, default: () => false },
});

const handleRowClick = (event) => {
  const row = event.data;
  props.onRowClick(row);
};
</script>

<template>
  <DataTable
    :value="posts"
    :loading="loading"
    :empty-state="emptyState"
    :infinite-scroll="{
      target: `#${drawerId}`,
      onLoadMore: onLoadMore,
      canLoadMore: canLoadMore,
      loadOnMount: false,
    }"
    class="pt-0! [&_tbody_td]:h-[112px]"
    clickable
    data-key="sourceId"
    @row-click="handleRowClick"
  >
    <Column field="title" header="Name or Handle" class="min-w-[280px]!">
      <template #body="{ data }">
        <RowTitle
          :media="data.src"
          :title="data.title"
          :subtitle="data.postDate"
          :avatar="data.avatar"
        />
      </template>
    </Column>

    <Column field="caption" :header="captionHeader" class="max-w-[400px]!">
      <template #body="{ data }">
        <div v-dash-tooltip="data.displayCaption" class="line-clamp-5">
          {{ data.displayCaption }}
        </div>
      </template>
    </Column>

    <Column header="Mention Type" class="w-[140px]!">
      <template #body="{ data }">
        <span>{{ data.mentionType }}</span>
      </template>
    </Column>

    <Column field="channel" header="Channel" class="w-[120px]!">
      <template #body="{ data }">
        <div class="flex items-center gap-2">
          <ChannelIcon :channel="data.channel" size="lg" />
          <span>{{ data.channelName }}</span>
        </div>
      </template>
    </Column>

    <Column header="Sentiment" class="w-[140px]!">
      <template #body="{ data }">
        <div v-if="data.sentiment" class="flex items-center gap-2" @click.stop>
          <ProgressSpinner v-if="isSentimentLoading(data)" size="12" />
          <SentimentSelect
            v-dash-tooltip="data.sentimentTooltip"
            :model-value="data.sentiment"
            :readonly="data.sentimentReadonly"
            @update:model-value="onSentimentUpdate(data, $event)"
          />
        </div>
        <Chip v-else-if="data.sentimentChip" v-bind="data.sentimentChip" />
      </template>
    </Column>
  </DataTable>
</template>
